import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MainLayoutComponent } from '../../../_layouts/main-layout/main-layout.component';
import { environment } from '../../../../../environments/environment';
import { AlertService } from '../../../_services/alert.service';
import { UserService } from '../../../_services/users.service';
import { InventoryApiRow, InventoryQuery, InventoryService } from '../../../_services/inventory.service';
import { RecordNavComponent, confirmLeave } from '../../../../Shared/_components/record-nav/record-nav.component';
import { StockHistoryComponent } from '../stock-history/stock-history.component';

// Quantities this large are stand-ins for "made to order", not real stock.
export const PLACEHOLDER_QTY = 100000;

// The Inventory list's search / filters / sort / paging, carried in the
// address so this page can step through the same items (Previous / Next)
// and send you back to the list exactly as you left it.
export interface InventoryListState {
  q?: string;
  cat?: string;
  status?: string;
  store?: string;
  sale?: string;
  sort?: string;
  group?: string;
  page?: string;
  size?: string;
}

// One item's stock (per yard) and prices, with the reason for a stock change
// kept in its stock history. /inventory/edit/:variantId
@Component({
    selector: 'app-inventory-edit',
    imports: [CommonModule, FormsModule, RouterLink, MainLayoutComponent, MatIconModule, MatTooltipModule, MatProgressSpinnerModule, RecordNavComponent],
    templateUrl: './inventory-edit.component.html'
})
export class InventoryEditComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private inventoryService = inject(InventoryService);
  private alertService = inject(AlertService);
  private userService = inject(UserService);

  // The purchasing team can look but not change anything.
  readonly canEdit = (() => {
    const roles = String(this.userService.isLoggedIn().userrole ?? '').split(',').map((r: string) => r.trim());
    return ['S-ADMIN', 'ADMIN', 'SALEADMIN', 'MARKETING'].some((r) => roles.includes(r));
  })();

  readonly reasons = ['Stock count', 'Delivery received', 'Damaged or scrapped', 'Customer return', 'Transfer between yards', 'Correction', 'Other'];

  // Storefront product page base, e.g. https://nustone.co.uk/product/
  readonly frontEndURL = environment.frontEndURL;

  variantId = 0;
  listState: InventoryListState = {};
  loading = true;
  notFound = false;
  row: InventoryApiRow | null = null;
  canSeeCost = false;

  // Previous / next, in the list's order and filters.
  nav: { productVariantId: number; productName: string; label: string }[] = [];

  // ---- form ----
  kettering = 0;
  markstey = 0;
  regularPrice: number | null = null;
  onSale = false;
  discountedPrice: number | null = null;
  costPrice: number | null = null;
  // Extreme Products: another variant of this product (null = none).
  extremeProductVariantId: number | null = null;
  variantOptions: { productVariantId: number; sku: string; label: string }[] = [];
  reason = '';
  otherReason = '';
  saving = false;
  tried = false;

  ngOnInit(): void {
    this.variantId = Number(this.route.snapshot.paramMap.get('id'));
    const qp = this.route.snapshot.queryParamMap;
    for (const k of ['q', 'cat', 'status', 'store', 'sale', 'sort', 'group', 'page', 'size'] as const) {
      const v = qp.get(k);
      if (v !== null) this.listState[k] = v;
    }
    this.loadItem();
    this.loadNav();
  }

  private get listQuery(): InventoryQuery {
    const s = this.listState;
    return {
      pageIndex: 0,
      pageSize: 100000,
      search: s.q || undefined,
      category: s.cat || 'ALL',
      status: s.status || 'ALL',
      store: s.store || 'ALL',
      onSale: s.sale === '1' || undefined,
      sort: s.sort || 'name',
      groupByProduct: s.group === '1',
    };
  }

  private loadItem(): void {
    this.loading = true;
    this.inventoryService.getInventory({ pageIndex: 0, pageSize: 1, variantId: this.variantId }).subscribe({
      next: (r) => {
        this.canSeeCost = r.canSeeCost;
        this.variantOptions = r.variantOptions ?? [];
        this.row = r.items[0] ?? null;
        this.notFound = !this.row;
        if (this.row) this.fillForm(this.row);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.notFound = true;
        this.alertService.alert('Could not load this item', 'error');
      },
    });
  }

  private loadNav(): void {
    this.inventoryService.getInventory(this.listQuery).subscribe({
      next: (r) => (this.nav = r.items.map((i) => ({ productVariantId: i.productVariantId, productName: i.productName, label: i.label || i.sku }))),
      error: () => (this.nav = []),
    });
  }

  private fillForm(r: InventoryApiRow): void {
    this.kettering = r.kStockQty;
    this.markstey = r.mStockQty;
    this.regularPrice = Number(r.price);
    this.onSale = !!r.discPrice;
    this.discountedPrice = r.discPrice ? Number(r.discPrice) : null;
    this.costPrice = r.costPrice !== null && r.costPrice !== undefined ? Number(r.costPrice) : null;
    this.extremeProductVariantId = r.extremeProductVariantId ?? null;
    this.reason = '';
    this.otherReason = '';
    this.tried = false;
  }

  // ---- previous / next ----
  get position(): number {
    return this.nav.findIndex((n) => n.productVariantId === this.variantId);
  }

  get prev() {
    return this.position > 0 ? this.nav[this.position - 1] : null;
  }

  get next() {
    const i = this.position;
    return i >= 0 && i < this.nav.length - 1 ? this.nav[i + 1] : null;
  }

  navName(n: { productName: string; label: string } | null): string | null {
    return n ? `${n.productName}${n.label ? ' - ' + n.label : ''}` : null;
  }

  goTo(n: { productVariantId: number; productName: string; label: string } | null): void {
    if (!n || this.saving) return;
    confirmLeave(this.dialog, this.changed, this.row?.productName ?? '', this.navName(n)!).subscribe((ok) => {
      if (ok) this.router.navigate(['/inventory/edit', n.productVariantId], { queryParams: this.listState });
    });
  }

  backToList(): void {
    this.router.navigate(['/inventory'], { queryParams: this.listState });
  }

  // ---- figures ----
  get madeToOrder(): boolean {
    return !!this.row && (this.row.kStockQty >= PLACEHOLDER_QTY || this.row.mStockQty >= PLACEHOLDER_QTY);
  }

  get total(): number {
    return (Number(this.kettering) || 0) + (Number(this.markstey) || 0);
  }

  get stockChanged(): boolean {
    return !!this.row && (Number(this.kettering) !== this.row.kStockQty || Number(this.markstey) !== this.row.mStockQty);
  }

  get priceChanged(): boolean {
    const r = this.row;
    if (!r) return false;
    return (
      Number(this.regularPrice) !== Number(r.price) ||
      (this.onSale ? Number(this.discountedPrice) || null : null) !== (r.discPrice ? Number(r.discPrice) : null) ||
      (this.canSeeCost && (this.costPrice ?? null) !== (r.costPrice !== null && r.costPrice !== undefined ? Number(r.costPrice) : null))
    );
  }

  get extremeChanged(): boolean {
    return !!this.row && (this.extremeProductVariantId ?? null) !== (this.row.extremeProductVariantId ?? null);
  }

  get changed(): boolean {
    return this.stockChanged || this.priceChanged || this.extremeChanged;
  }

  optionName(o: { sku: string; label: string }): string {
    return o.label ? `${o.label} (${o.sku})` : o.sku;
  }

  adjust(store: 'kettering' | 'markstey', by: number): void {
    this[store] = Math.max(0, (Number(this[store]) || 0) + by);
  }

  // What the customer pays, and how far below the regular price that is.
  get sellingPrice(): number {
    return this.onSale && this.discountedPrice ? Number(this.discountedPrice) : Number(this.regularPrice) || 0;
  }

  get discountPercent(): number | null {
    const reg = Number(this.regularPrice);
    return this.onSale && this.discountedPrice && reg > 0 ? Math.round((1 - Number(this.discountedPrice) / reg) * 100) : null;
  }

  // Price minus cost, as a share of the price (as entered - VAT is not taken off).
  get margin(): number | null {
    return this.canSeeCost && this.costPrice !== null && this.sellingPrice > 0 ? Math.round(((this.sellingPrice - Number(this.costPrice)) / this.sellingPrice) * 100) : null;
  }

  get stockStatus(): 'OUT' | 'LOW' | 'IN' | 'MTO' {
    if (this.madeToOrder) return 'MTO';
    if (this.total <= 0) return 'OUT';
    return this.total < 5 ? 'LOW' : 'IN';
  }

  get errors(): { stock: string; price: string; discount: string; cost: string } {
    const whole = (n: any) => Number.isInteger(Number(n)) && Number(n) >= 0;
    return {
      stock: whole(this.kettering) && whole(this.markstey) ? '' : 'Stock must be a whole number, zero or more',
      price: Number(this.regularPrice) > 0 ? '' : 'The regular price must be more than zero',
      discount: !this.onSale ? '' : !(Number(this.discountedPrice) > 0) ? 'Enter the sale price' : Number(this.discountedPrice) >= Number(this.regularPrice) ? 'The sale price must be lower than the regular price' : '',
      cost: !this.canSeeCost || this.costPrice === null || Number(this.costPrice) >= 0 ? '' : 'Cost can not be negative',
    };
  }

  get valid(): boolean {
    const e = this.errors;
    return !e.stock && !e.price && !e.discount && !e.cost;
  }

  // A stock change should say why; a price-only change doesn't need to.
  get reasonMissing(): boolean {
    return this.stockChanged && !this.reason;
  }

  get blocker(): string | null {
    if (!this.valid) return Object.values(this.errors).find(Boolean) || null;
    if (this.reasonMissing) return 'Pick why the stock is changing';
    return null;
  }

  discard(): void {
    if (this.row) this.fillForm(this.row);
  }

  // `thenNext`: move on to the next item once saved (stock counts, price runs).
  save(thenNext = false): void {
    this.tried = true;
    const r = this.row;
    if (!r || !this.canEdit || !this.valid || this.reasonMissing || !this.changed || this.saving) return;
    const reason = this.reason === 'Other' ? this.otherReason.trim() || 'Other' : this.reason;
    const cost = !this.canSeeCost ? undefined : this.costPrice === null || (this.costPrice as any) === '' ? null : Number(this.costPrice);
    this.saving = true;
    this.inventoryService
      .updateVariant(r.productVariantId, {
        kStockQty: Number(this.kettering),
        mStockQty: Number(this.markstey),
        price: Number(this.regularPrice),
        discPrice: this.onSale ? Number(this.discountedPrice) : null,
        // Not sent at all by someone who can't see it, so it is never touched.
        costPrice: cost,
        reason: this.stockChanged ? reason : undefined,
        extremeProductVariantId: this.extremeChanged ? this.extremeProductVariantId : undefined,
      })
      .subscribe({
        next: () => {
          this.saving = false;
          this.alertService.alert(`${r.productName} updated`, 'success');
          // What's saved is now the baseline.
          this.row = {
            ...r,
            kStockQty: Number(this.kettering),
            mStockQty: Number(this.markstey),
            stockQty: this.total,
            price: Number(this.regularPrice),
            discPrice: this.onSale ? Number(this.discountedPrice) : null,
            costPrice: cost === undefined ? r.costPrice : cost,
            extremeProductVariantId: this.extremeProductVariantId,
          };
          this.fillForm(this.row);
          if (thenNext && this.next) this.router.navigate(['/inventory/edit', this.next.productVariantId], { queryParams: this.listState });
        },
        error: (error) => {
          this.saving = false;
          this.alertService.alert(error.error?.message || `Could not save ${r.productName}`, 'error');
        },
      });
  }

  openHistory(): void {
    const r = this.row;
    if (!r) return;
    this.dialog.open(StockHistoryComponent, {
      width: '1000px',
      maxWidth: '96vw',
      data: { productVariantId: r.productVariantId, productName: r.productName, sku: r.sku, label: r.label, barcode: r.barcode, image: r.image },
    });
  }

  imageOf(r: InventoryApiRow): string | null {
    return r.image ? (r.image.includes(',') ? r.image.split(',')[0] : r.image) : null;
  }
}
